import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BancoPlazaService,
  BancoPlazaHealth,
  BancoPlazaCuenta,
  BancoPlazaBalance,
  BancoPlazaStats,
  BancoPlazaTransaction,
  ListTransactionsParams,
} from '../../services/banco-plaza.service';
import { BancoPlazaConsultaLiqModalComponent } from './banco-plaza-consulta-liq-modal/banco-plaza-consulta-liq-modal.component';
import { BancoPlazaTransactionDetailModalComponent } from './banco-plaza-transaction-detail-modal/banco-plaza-transaction-detail-modal.component';
import { BancoPlazaMontoMinModalComponent } from './banco-plaza-monto-min-modal/banco-plaza-monto-min-modal.component';
import { RegistrarReferenciaOperadorModalComponent } from '../referencias-ves-admin/registrar-referencia-operador-modal/registrar-referencia-operador-modal.component';

@Component({
  selector: 'app-banco-plaza-home',
  templateUrl: './banco-plaza-home.component.html',
  styleUrls: ['./banco-plaza-home.component.sass'],
})
export class BancoPlazaHomeComponent implements OnInit, OnDestroy {
  // Health
  health: BancoPlazaHealth | null = null;
  healthLoading = true;
  healthError: string | null = null;

  // Cuentas (saldo real banco vía /cuentas/default)
  cuentas: BancoPlazaCuenta[] = [];
  cuentasLoading = true;

  // Balance operacional dual (disponible/retenido)
  balance: BancoPlazaBalance | null = null;
  balanceLoading = true;

  // Stats KPIs del día
  stats: BancoPlazaStats | null = null;
  statsLoading = true;

  // Cuota diaria USD (cap contractual BP)
  cuota: {
    limiteOperacionUSD: number;
    limiteDiarioUSD: number;
    tasaBcvUsada: number | null;
    acumuladoUSD: number;
    disponibleUSD: number;
    porcentajeUsado: number;
    operacionesHoy: number;
    totalVesHoy: number;
    ventanaDesde: string;
    ventanaHasta: string;
  } | null = null;
  cuotaLoading = true;

  // Transactions paginadas
  transactions: BancoPlazaTransaction[] = [];
  txLoading = false;
  txTotal = 0;
  txPage = 1;
  txPages = 1;
  txLimit = 20;

  // Filtros
  filterTipo = '';
  filterStatus = '';
  filterReferencia = '';
  filterCodBancoDestino = '';
  filterFechaDesde = '';
  filterFechaHasta = '';

  // Cron triggers
  syncing = false;
  settlementChecking = false;
  /** @disabled F12.1 — código vivo; flipear a true acá + BP_SYNC_HISTORICO_HABILITADO en DB para re-habilitar */
  syncingHistorico = false;
  /**
   * @disabled F12.1 — Default false: oculta el botón en el dashboard.
   * Cambiar a `true` (+ poner `BP_SYNC_HISTORICO_HABILITADO='true'` en
   * parametros DB) si reaparece un gap de sincronización y hay que re-correr
   * el backfill. Ver feedback_features_one_shot_deshabilitar.
   */
  syncHistoricoHabilitado = false;

  // Export XLS
  exportingXls = false;

  /**
   * Estado de verificación BCV por txId (botón "Verificar BCV" en cada fila).
   * Se llena bajo demanda; persiste solo mientras la página está abierta.
   * No se guarda en BD — es una consulta read-only contra el banco.
   *
   * Tipo `{ [k]: T | undefined }` (en vez de `Record<string, T>`) para que
   * el strict template type checker de Angular acepte `bcvVerification[tx.id]?.x`
   * sin warnings NG8107 — en runtime sí puede ser undefined si la key no existe.
   */
  bcvVerification: {
    [txId: string]:
      | {
          loading?: boolean;
          result?: 'aprobada' | 'pendiente' | 'rechazada';
          code?: string;
          desc?: string | null;
          consultadoEn?: string;
          error?: string;
        }
      | undefined;
  } = {};

  private sub = new Subscription();
  private healthInterval: any;

  constructor(
    private bp: BancoPlazaService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadHealth();
    this.loadCuentas();
    this.loadBalance();
    this.loadStats();
    this.loadCuota();
    this.loadTransactions();

    // Polling cada 30s del health + cuota (sin websocket por ahora)
    this.healthInterval = setInterval(() => {
      this.loadHealth(true);
      this.loadCuota(true);
    }, 30_000);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.healthInterval) clearInterval(this.healthInterval);
  }

  // ─── Loaders ────────────────────────────────────────────────────

  loadHealth(silent = false): void {
    if (!silent) this.healthLoading = true;
    this.sub.add(
      this.bp.getHealth().subscribe({
        next: (h) => {
          this.health = h;
          this.healthLoading = false;
          this.healthError = null;
        },
        error: (err) => {
          this.healthError = typeof err === 'string' ? err : 'Error consultando health';
          this.healthLoading = false;
        },
      }),
    );
  }

  loadCuentas(): void {
    this.cuentasLoading = true;
    this.sub.add(
      this.bp.getCuentasDefault().subscribe({
        next: (res) => {
          this.cuentas = res.data?.cuentas || [];
          this.cuentasLoading = false;
        },
        error: () => {
          this.cuentas = [];
          this.cuentasLoading = false;
        },
      }),
    );
  }

  loadBalance(): void {
    this.balanceLoading = true;
    this.sub.add(
      this.bp.getBalance().subscribe({
        next: (res) => {
          this.balance = res.data;
          this.balanceLoading = false;
        },
        error: () => {
          this.balance = null;
          this.balanceLoading = false;
        },
      }),
    );
  }

  loadStats(): void {
    this.statsLoading = true;
    this.sub.add(
      this.bp.getStats().subscribe({
        next: (res) => {
          this.stats = res.data;
          this.statsLoading = false;
        },
        error: () => {
          this.stats = null;
          this.statsLoading = false;
        },
      }),
    );
  }

  /**
   * Carga la cuota diaria USD. `silent=true` evita flashear el loader
   * cuando lo dispara el polling periódico.
   */
  loadCuota(silent = false): void {
    if (!silent) this.cuotaLoading = true;
    this.sub.add(
      this.bp.getCuotaDiariaUsd().subscribe({
        next: (res) => {
          this.cuota = res.data;
          this.cuotaLoading = false;
        },
        error: () => {
          this.cuota = null;
          this.cuotaLoading = false;
        },
      }),
    );
  }

  /**
   * Color para la barra de progreso de la cuota según el % usado:
   * verde <70%, amarillo 70-90%, rojo >90%. Refleja urgencia operacional.
   */
  getCuotaBarColor(): string {
    const p = this.cuota?.porcentajeUsado || 0;
    if (p >= 90) return '#dc2626';
    if (p >= 70) return '#d97706';
    return '#16a34a';
  }

  loadTransactions(): void {
    this.txLoading = true;
    const params: ListTransactionsParams = {
      page: this.txPage,
      limit: this.txLimit,
    };
    if (this.filterTipo) params.tipo = this.filterTipo as any;
    if (this.filterStatus) params.status = this.filterStatus as any;
    if (this.filterReferencia.trim()) params.referencia = this.filterReferencia.trim();
    if (this.filterCodBancoDestino.trim()) params.codBancoDestino = this.filterCodBancoDestino.trim();
    if (this.filterFechaDesde) params.fechaDesde = this.filterFechaDesde;
    if (this.filterFechaHasta) params.fechaHasta = this.filterFechaHasta;

    this.sub.add(
      this.bp.getTransactions(params).subscribe({
        next: (res) => {
          this.transactions = res.data || [];
          this.txTotal = res.total || 0;
          this.txPages = res.totalPages || 1;
          this.txLoading = false;
        },
        error: () => {
          this.transactions = [];
          this.txLoading = false;
        },
      }),
    );
  }

  // ─── Filtros + paginación ───────────────────────────────────────

  applyFilters(): void {
    this.txPage = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.filterTipo = '';
    this.filterStatus = '';
    this.filterReferencia = '';
    this.filterCodBancoDestino = '';
    this.filterFechaDesde = '';
    this.filterFechaHasta = '';
    this.txPage = 1;
    this.loadTransactions();
  }

  /**
   * Descarga las transacciones que matchean los filtros actuales como XLSX.
   * Backend protegido por `AguacateOperatorGuard`. Si el dataset > 50k filas,
   * el server devuelve 413 y mostramos snackbar pidiendo acotar filtros.
   */
  exportarXls(): void {
    if (this.exportingXls) return;
    this.exportingXls = true;
    const params: ListTransactionsParams = {
      tipo: (this.filterTipo as any) || undefined,
      status: (this.filterStatus as any) || undefined,
      referencia: this.filterReferencia || undefined,
      codBancoDestino: this.filterCodBancoDestino || undefined,
      fechaDesde: this.filterFechaDesde || undefined,
      fechaHasta: this.filterFechaHasta || undefined,
    };
    this.sub.add(
      this.bp.exportTransactions(params).subscribe({
        next: (blob) => {
          this.exportingXls = false;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
          a.download = `banco-plaza-tx-${ts}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.snack.open('Archivo descargado', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.exportingXls = false;
          // El handleError del service ya colapsa el error a string.
          // Si el backend devolvió 413, lo detectamos por el texto.
          const msg = String(err);
          if (msg.includes('413') || msg.toLowerCase().includes('excede')) {
            this.snack.open(
              'El export excede 50.000 filas — acotá filtros (fecha/tipo/status)',
              'OK',
              { duration: 6000 },
            );
          } else {
            this.snack.open(`Error exportando: ${msg}`, 'OK', { duration: 5000 });
          }
        },
      }),
    );
  }

  goPage(page: number): void {
    if (page < 1 || page > this.txPages) return;
    this.txPage = page;
    this.loadTransactions();
  }

  // ─── Acciones ───────────────────────────────────────────────────

  forzarPayinSync(): void {
    this.syncing = true;
    this.sub.add(
      this.bp.forzarPayinSync().subscribe({
        next: (res) => {
          this.syncing = false;
          this.snack.open(
            `Payin sync: ${res.data?.payins ?? 0} payins, ${res.data?.payouts ?? 0} payouts registrados`,
            'OK',
            { duration: 4000 },
          );
          this.loadStats();
          this.loadBalance();
          this.loadTransactions();
        },
        error: (err) => {
          this.syncing = false;
          this.snack.open(`Error: ${err}`, 'OK', { duration: 5000 });
        },
      }),
    );
  }

  forzarSettlement(): void {
    this.settlementChecking = true;
    this.sub.add(
      this.bp.forzarSettlement().subscribe({
        next: (res) => {
          this.settlementChecking = false;
          this.snack.open(
            `Settlement: ${res.data?.processed ?? 0} procesadas (${res.data?.approved ?? 0} approved, ${res.data?.rejected ?? 0} rejected, ${res.data?.expired ?? 0} expired)`,
            'OK',
            { duration: 5000 },
          );
          this.loadStats();
          this.loadBalance();
          this.loadTransactions();
        },
        error: (err) => {
          this.settlementChecking = false;
          this.snack.open(`Error: ${err}`, 'OK', { duration: 5000 });
        },
      }),
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // @disabled F12.1 SYNC HISTÓRICO — código MANTENIDO. Para re-habilitar:
  //   1. UPDATE parametros SET valor='true'
  //        WHERE descripcion='BP_SYNC_HISTORICO_HABILITADO';
  //   2. En este archivo, cambiar `syncHistoricoHabilitado = true`.
  //   3. Re-deploy front.
  // Disparo manual one-shot del sync histórico de movimientos BP desde
  // fechaApertura hasta hoy. Idempotente por dedup natural de referencia_banco.
  // Ver feedback_features_one_shot_deshabilitar.
  // ════════════════════════════════════════════════════════════════════
  /** @disabled F12.1 — habilitar vía syncHistoricoHabilitado + parámetro DB */
  forzarSyncHistorico(): void {
    const confirmar = window.confirm(
      'SINCRONIZAR HISTÓRICO DE MOVIMIENTOS\n\n' +
        'Esto consulta al banco mes por mes desde la fecha de apertura\n' +
        'de la cuenta hasta hoy y persiste los movimientos faltantes.\n\n' +
        '- Tarda ~3-5 minutos (depende de cuántos meses)\n' +
        '- Solo debe ejecutarse 1 vez (post-deploy inicial)\n' +
        '- Re-ejecutarla es seguro (dedup) pero genera carga al banco\n' +
        '- No interfiere con el cron normal (lock automático)\n\n' +
        '¿Continuar?',
    );
    if (!confirmar) return;

    this.syncingHistorico = true;
    this.sub.add(
      this.bp.forzarSyncHistorico().subscribe({
        next: (res) => {
          this.syncingHistorico = false;
          const d = res.data;
          this.snack.open(
            `Histórico sincronizado: ${d.mesesProcesados} meses, ` +
              `${d.payinsTotal} payins, ${d.payoutsTotal} payouts. ` +
              `${d.mesesFallidos > 0 ? `${d.mesesFallidos} meses con error.` : ''}`,
            'OK',
            { duration: 10000 },
          );
          this.loadStats();
          this.loadBalance();
          this.loadTransactions();
        },
        error: (err) => {
          this.syncingHistorico = false;
          this.snack.open(`Error sync histórico: ${err}`, 'OK', { duration: 8000 });
        },
      }),
    );
  }
  // ════════════════════════════════════════════════════════════════════
  // Fin bloque @disabled F12.1
  // ════════════════════════════════════════════════════════════════════

  refreshAll(): void {
    this.loadHealth();
    this.loadCuentas();
    this.loadBalance();
    this.loadStats();
    this.loadCuota();
    this.loadTransactions();
  }

  /**
   * Abre modal para editar BANCO_PLAZA_MONTO_MIN (tope nominal VES de BP).
   * Refresca balance/stats post-cierre por si afecta vistas dependientes.
   */
  abrirMontoMinModal(): void {
    const ref = this.dialog.open(BancoPlazaMontoMinModalComponent, {
      width: '520px',
      panelClass: 'rounded-dialog',
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated) {
        this.loadStats();
      }
    });
  }

  /**
   * Abre el modal de registrar referencia bloqueado en rail BANCO_PLAZA. La
   * vista /admin/banco-plaza es específica de este banco, por eso el selector
   * de rail queda oculto y se fija BP automáticamente.
   */
  openRegistrarReferenciaModal(): void {
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(RegistrarReferenciaOperadorModalComponent, {
      data: { lockedRail: 'BANCO_PLAZA' },
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.loadTransactions();
        this.loadStats();
      }
    });
  }

  // ─── Modales ────────────────────────────────────────────────────

  openConsultaLiqModal(): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(BancoPlazaConsultaLiqModalComponent, {
      maxWidth: isMobile ? '95vw' : '700px',
      minWidth: isMobile ? '90vw' : '550px',
    });
  }

  openTransactionDetail(tx: BancoPlazaTransaction): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(BancoPlazaTransactionDetailModalComponent, {
      maxWidth: isMobile ? '98vw' : '900px',
      minWidth: isMobile ? '95vw' : '700px',
      data: { id: tx.id },
    });
  }

  // ─── Helpers UI ─────────────────────────────────────────────────

  getStatusClass(status: string): string {
    // Mantengo el convenio existente del .sass (underscores intactos):
    //   PENDING_BCV → .status-pending_bcv
    //   PENDING_VERIFICATION → .status-pending_verification
    return `status-${status.toLowerCase()}`;
  }

  getStatusLabel(status: string): string {
    return {
      PENDING: 'Pendiente',
      PENDING_BCV: 'Pendiente BCV',
      PENDING_VERIFICATION: 'Verificando',
      APPROVED: 'Aprobada',
      REJECTED: 'Rechazada',
      EXPIRED: 'Expirada',
    }[status] || status;
  }

  /**
   * Cualquier `PAYOUT*` (incluyendo el legacy `PAYOUT` y los nuevos
   * OWNBANK/OTHERBANK/P2P) muestra flecha hacia afuera. Solo PAYIN
   * muestra flecha hacia adentro.
   */
  getTipoIcon(tipo: string): string {
    return tipo === 'PAYIN' ? 'arrow_downward' : 'arrow_upward';
  }

  getTipoClass(tipo: string): string {
    if (tipo === 'PAYIN') return 'tx-payin';
    if (tipo === 'CARGO_BANCARIO') return 'tx-cargo';
    return 'tx-payout';
  }

  /**
   * Texto amigable del tipo de transacción para el listado.
   */
  getTipoLabel(tipo: string): string {
    return {
      PAYIN: 'Payin',
      PAYOUT: 'Payout',
      PAYOUT_OWNBANK: 'Payout · Mismo banco',
      PAYOUT_OTHERBANK: 'Payout · Otro banco',
      PAYOUT_P2P: 'Payout · Pago Móvil',
      CARGO_BANCARIO: 'Cargo del banco',
    }[tipo] || tipo;
  }

  /**
   * Verdadero solo para payouts iniciados por nosotros (PAYOUT + PAYOUT_*).
   * `PAYIN` y `CARGO_BANCARIO` retornan false — el cargo del banco es débito
   * pero NO un payout nuestro.
   */
  isPayout(tipo: string): boolean {
    return tipo === 'PAYOUT' || tipo.startsWith('PAYOUT_');
  }

  /**
   * Verdadero para cualquier movimiento que reste saldo de nuestra cuenta:
   * PAYOUT* o CARGO_BANCARIO. Usado para signo `-` y clases de color.
   */
  isDebit(tipo: string): boolean {
    return tipo !== 'PAYIN';
  }

  // ─── Verificar BCV (botón en fila de payout) ────────────────────

  /**
   * Solo se puede verificar contra BCV los payouts interbancarios con
   * referenciaBanco. Los intrabancarios (OWNBANK) y P2P no pasan por BCV.
   * Los PAYIN y CARGO_BANCARIO no son payouts iniciados por nosotros.
   */
  puedeVerificarBcv(tx: BancoPlazaTransaction): boolean {
    if (!tx.referenciaBanco) return false;
    return tx.tipo === 'PAYOUT_OTHERBANK' || tx.tipo === 'PAYOUT';
  }

  /**
   * Consulta directa al banco usando los datos del log. Resultado read-only,
   * NO modifica el log. Se muestra como badge persistente en la fila hasta
   * cerrar la página o refrescar la lista.
   *
   * Frena propagación porque la fila tiene click → openTransactionDetail.
   */
  verificarBcv(tx: BancoPlazaTransaction, event: Event): void {
    event.stopPropagation();
    if (!this.puedeVerificarBcv(tx)) return;
    if (this.bcvVerification[tx.id]?.loading) return;

    this.bcvVerification[tx.id] = { loading: true };
    this.sub.add(
      this.bp.verificarBcv(tx.id).subscribe({
        next: (res) => {
          const d = res.data;
          const result: 'aprobada' | 'pendiente' | 'rechazada' = d.esAprobada
            ? 'aprobada'
            : d.esPendiente
              ? 'pendiente'
              : 'rechazada';
          this.bcvVerification[tx.id] = {
            loading: false,
            result,
            code: d.codigoRespuesta,
            desc: d.descripcionCliente || d.descripcionSistema,
            consultadoEn: d.consultadoEn,
          };
          const labels = {
            aprobada: '✅ Aprobada por BCV',
            pendiente: '⏳ Pendiente BCV',
            rechazada: `❌ Rechazada (${d.codigoRespuesta})`,
          };
          this.snack.open(
            `${labels[result]} — ref ${d.referenciaBanco}`,
            'OK',
            { duration: 6000 },
          );
        },
        error: (err) => {
          const errMsg = typeof err === 'string' ? err : 'Error verificando BCV';
          this.bcvVerification[tx.id] = {
            loading: false,
            error: errMsg,
          };
          this.snack.open(
            `Error verificando BCV: ${errMsg}`,
            'OK',
            { duration: 5000 },
          );
        },
      }),
    );
  }

  getBcvBadgeClass(txId: string): string {
    const r = this.bcvVerification[txId]?.result;
    if (r === 'aprobada') return 'bcv-badge-ok';
    if (r === 'pendiente') return 'bcv-badge-pending';
    if (r === 'rechazada') return 'bcv-badge-reject';
    return '';
  }

  getBcvBadgeLabel(txId: string): string {
    const v = this.bcvVerification[txId];
    if (!v) return '';
    if (v.error) return `BCV: error`;
    if (v.loading) return 'BCV…';
    if (v.result === 'aprobada') return 'BCV ✓';
    if (v.result === 'pendiente') return 'BCV 9999';
    if (v.result === 'rechazada') return `BCV ${v.code}`;
    return '';
  }

}
